import React from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Dropdown,
  Input,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslate } from '@/translations';
import type {
  JobChainTemplateConditionField,
  TemplateCondition,
  TemplateEdge,
} from '@/api/JobChainTemplates';
import {
  conditionFieldLabel,
  conditionOperatorLabel,
  conditionValues,
  createEdgeCondition,
  edgeConditionSummary,
  isMultipleCondition,
  isNullCondition,
  nextConditionGroupId,
} from '@/modules/SystemAdministration/Jobs/JobChainTemplates/edgeConditions';

const { Text, Paragraph } = Typography;

export const EdgeConditionsEditor = ({
  edge,
  fields,
  editable,
  onChange,
}: {
  edge: TemplateEdge;
  fields: JobChainTemplateConditionField[];
  editable: boolean;
  onChange: (edge: TemplateEdge) => void;
}) => {
  const { t } = useTranslate();
  const groups = [
    ...new Set(edge.conditions.map((condition) => condition.groupId)),
  ];
  const replaceConditions = (conditions: TemplateCondition[]) => {
    if (editable) onChange({ ...edge, conditions });
  };
  const update = (index: number, patch: Partial<TemplateCondition>) =>
    replaceConditions(
      edge.conditions.map((condition, row) =>
        row === index ? { ...condition, ...patch } : condition,
      ),
    );
  const add = (groupId: number) => {
    if (fields[0])
      replaceConditions([
        ...edge.conditions,
        createEdgeCondition(groupId, fields[0]),
      ]);
  };

  return (
    <div className="edge-conditions">
      <Typography.Title level={5}>
        {t('jobChainTemplateConditionTitle')}
      </Typography.Title>
      {!edge.conditions.length ? (
        <Alert
          type="info"
          showIcon
          message={t('jobChainTemplateConditionAlwaysContinue')}
          description={t('jobChainTemplateConditionNoConditions')}
        />
      ) : (
        <div className="condition-summary">
          <Text type="secondary">
            {t('jobChainTemplateConditionContinueWhen')}
          </Text>
          <Paragraph
            key={edge.key}
            style={{ marginBottom: 0 }}
            ellipsis={{
              rows: 2,
              expandable: true,
              symbol: t('jobChainTemplateConditionShowFullRule'),
            }}
          >
            {edgeConditionSummary(edge.conditions, t)}
          </Paragraph>
        </div>
      )}

      {groups.map((groupId, groupIndex) => (
        <React.Fragment key={groupId}>
          {groupIndex > 0 && (
            <Divider plain className="condition-or">
              <Tag color="blue">{t('jobChainTemplateConditionOr')}</Tag>
            </Divider>
          )}
          <Card
            size="small"
            className="condition-alternative"
            title={
              <Space size={6}>
                <Tag>{t('jobChainTemplateConditionAll')}</Tag>
                <Text>{t('jobChainTemplateConditionMatchAll')}</Text>
              </Space>
            }
            extra={
              editable && (
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined aria-hidden="true" />}
                  aria-label={t(
                    'jobChainTemplateConditionRemoveAlternativeAria',
                    {
                      index: groupIndex + 1,
                    },
                  )}
                  onClick={() =>
                    replaceConditions(
                      edge.conditions.filter(
                        (condition) => condition.groupId !== groupId,
                      ),
                    )
                  }
                />
              )
            }
            styles={{ body: { padding: 10 } }}
          >
            {edge.conditions.map((condition, index) => {
              if (condition.groupId !== groupId) return null;
              const field = fields.find((item) => item.key === condition.name);
              const operators = [
                ...new Set([...(field?.operators ?? []), condition.condition]),
              ];
              const values = isMultipleCondition(condition.condition)
                ? conditionValues(condition.value)
                : [condition.value ?? ''];
              const valueOptions = [
                ...new Set([...(field?.allowedValues ?? []), ...values]),
              ]
                .filter((value) => value !== '')
                .map((value) => ({
                  value,
                  label: value,
                  disabled: !field?.allowedValues.includes(value),
                }));
              const fieldOptions = fields.map((item) => ({
                value: item.key,
                label: conditionFieldLabel(item.key, t),
              }));
              if (!fieldOptions.some((item) => item.value === condition.name))
                fieldOptions.push({
                  value: condition.name,
                  label: condition.name,
                });
              const otherGroups = groups.filter((id) => id !== groupId);
              return (
                <div
                  className="condition-row"
                  key={condition.id ?? `new-${index}`}
                  data-readonly={!editable}
                >
                  <div className="condition-fields">
                    <Select
                      className="condition-field"
                      showSearch
                      optionFilterProp="label"
                      id={`condition-${edge.key}-${index}`}
                      aria-label={t('jobChainTemplateConditionFieldAria', {
                        index: index + 1,
                      })}
                      value={condition.name}
                      options={fieldOptions}
                      disabled={!editable}
                      onChange={(value) => {
                        const next = fields.find((item) => item.key === value);
                        if (next) {
                          const replacement = createEdgeCondition(
                            condition.groupId,
                            next,
                          );
                          update(index, {
                            name: replacement.name,
                            condition: replacement.condition,
                            value: replacement.value,
                          });
                        }
                      }}
                    />
                    <Select
                      aria-label={t('jobChainTemplateConditionOperatorAria', {
                        index: index + 1,
                      })}
                      value={condition.condition}
                      disabled={!editable}
                      options={operators.map((operator) => ({
                        value: operator,
                        label: conditionOperatorLabel(operator, t),
                      }))}
                      onChange={(operator) => {
                        let value = condition.value;
                        if (isNullCondition(operator)) value = null;
                        else if (isNullCondition(condition.condition))
                          value = field?.allowedValues[0] ?? '';
                        else if (
                          isMultipleCondition(condition.condition) &&
                          !isMultipleCondition(operator)
                        )
                          value = conditionValues(value)[0] ?? '';
                        update(index, { condition: operator, value });
                      }}
                    />
                    {isNullCondition(condition.condition) ? (
                      <Text type="secondary" className="condition-no-value">
                        {t('jobChainTemplateConditionNoValueNeeded')}
                      </Text>
                    ) : !editable ? (
                      <Input
                        aria-label={t('jobChainTemplateConditionValueAria', {
                          index: index + 1,
                        })}
                        readOnly
                        value={condition.value ?? ''}
                        placeholder={
                          condition.value === null
                            ? t('jobChainTemplateConditionNullValue')
                            : ''
                        }
                      />
                    ) : isMultipleCondition(condition.condition) ? (
                      <Select
                        mode="multiple"
                        aria-label={t('jobChainTemplateConditionValueAria', {
                          index: index + 1,
                        })}
                        maxTagCount="responsive"
                        value={conditionValues(condition.value)}
                        options={valueOptions}
                        placeholder={t('jobChainTemplateConditionChooseValues')}
                        onChange={(value: string[]) =>
                          update(index, { value: value.join(',') })
                        }
                      />
                    ) : field?.allowedValues.length ? (
                      <Select
                        aria-label={t('jobChainTemplateConditionValueAria', {
                          index: index + 1,
                        })}
                        value={condition.value ?? undefined}
                        options={valueOptions}
                        placeholder={t('jobChainTemplateConditionChooseValue')}
                        onChange={(value) => update(index, { value })}
                      />
                    ) : (
                      <Input
                        aria-label={t('jobChainTemplateConditionValueAria', {
                          index: index + 1,
                        })}
                        value={condition.value ?? ''}
                        onChange={(event) =>
                          update(index, { value: event.target.value })
                        }
                      />
                    )}
                  </div>
                  {editable && (
                    <div className="condition-actions">
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined aria-hidden="true" />}
                        aria-label={t('jobChainTemplateConditionRemoveAria', {
                          index: index + 1,
                        })}
                        onClick={() =>
                          replaceConditions(
                            edge.conditions.filter((_, row) => row !== index),
                          )
                        }
                      />
                      {edge.conditions.length > 1 && (
                        <Dropdown
                          trigger={['click']}
                          menu={{
                            items: [
                              ...otherGroups.map((id) => ({
                                key: String(id),
                                label: t(
                                  'jobChainTemplateConditionMoveToAlternative',
                                  {
                                    index: groups.indexOf(id) + 1,
                                  },
                                ),
                              })),
                              {
                                key: 'new',
                                label: t(
                                  'jobChainTemplateConditionMoveToNewAlternative',
                                ),
                              },
                            ],
                            onClick: ({ key }) =>
                              update(index, {
                                groupId:
                                  key === 'new'
                                    ? nextConditionGroupId(edge.conditions)
                                    : Number(key),
                              }),
                          }}
                        >
                          <Button
                            type="text"
                            size="small"
                            icon={<MoreOutlined aria-hidden="true" />}
                            aria-label={t(
                              'jobChainTemplateConditionMoreActionsAria',
                              {
                                index: index + 1,
                              },
                            )}
                          />
                        </Dropdown>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {editable && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined aria-hidden="true" />}
                disabled={!fields.length}
                onClick={() => add(groupId)}
                aria-label={t('jobChainTemplateConditionAddToAlternativeAria', {
                  index: groupIndex + 1,
                })}
              >
                {t('jobChainTemplateConditionAddAnd')}
              </Button>
            )}
          </Card>
        </React.Fragment>
      ))}
      {editable && (
        <Button
          block
          type="dashed"
          icon={<PlusOutlined aria-hidden="true" />}
          className="condition-add-alternative"
          disabled={!fields.length}
          onClick={() => add(nextConditionGroupId(edge.conditions))}
        >
          {edge.conditions.length
            ? t('jobChainTemplateConditionAddAlternative')
            : t('jobChainTemplateConditionAdd')}
        </Button>
      )}
    </div>
  );
};
